/*
 * COPYRIGHT (c) 2023
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { Card, Icon, Popup, Tooltip, VerticalMenu } from '@scuf/common';
import './SummaryTableTheme.scss';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import { useTranslation } from '@scuf/localization';
import FilterComponent from './FilterComponent';
import ColumnSelectorComponent from './ColumnSelectorComponent';
import {
  IAny,
  IDataTableColumn,
  ISummaryTableDataObject
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  columnsMetaData,
  columnsMetaDataForOtherUtility
} from './columnMetaData';
import CSVDownloadComponent from './CSVDownloadComponent';
import { formatDate } from '../../../DetailKPIWidgets/UtilityBillViewDetailsComponent/UtilityBillViewDetailsUtils';
import { ApiContext } from '../../../../context/ApiContext';
import ElectricityIconUtility from '../../../../images/ElectricityIconUtility.svg';
import GasIcon from '../../../../images/GasIcon.svg';
import WaterIcon from '../../../../images/WaterIcon.svg';
import SewerIcon from '../../../../images/sewerage_Icon.svg';
import areIntervalsOverlapping from 'date-fns/esm/fp/areIntervalsOverlapping/index.js';
import _ from 'lodash';
import { UtilityType } from '../../Utils/Constants';

export interface IRawTableColumn {
  name: string;
  unit: string | null;
  key: string;
}
export interface IUtilitySummaryProps {
  rowData: ISummaryTableDataObject[];
  columnDetails: IRawTableColumn[];
  fileName: string;
  costCurrency?: string;
  utilityType?: string;
  isMonthly?: boolean;
}

const iconMapper: { [key: string]: IAny } = {
  Electricity: ElectricityIconUtility,
  Water: WaterIcon,
  Gas: GasIcon,
  Sewer: SewerIcon,
  electric: ElectricityIconUtility
};

export interface ISummaryTableColumn extends IDataTableColumn {
  unit?: string | null;
  isSelected?: boolean;
  isListed?: boolean;
  isTabulated?: boolean;
  order?: number;
  costField?: boolean;
  cellDataRenderer?: (cellData: ICellData) => JSX.Element;
}

const OTHER_COLUMNS = ['fixed', 'taxes', 'penalties'];
const OTHERS = 'others';

const renderBillingCycle = (cellData: ICellData) => {
  const {
    billingCycleStartDate,
    billingCycleEndDate,
    billingCycleNumberOfDays
  } = cellData.rowData;
  const billingCycle = `${formatDate(billingCycleStartDate)} - ${formatDate(
    billingCycleEndDate
  )} (${billingCycleNumberOfDays} days)`;
  return <span>{billingCycle}</span>;
};

const otherColumnObj = {
  header: 'Others (Fixed, Penalties, Taxes)',
  unit: null,
  field: 'others',
  isSelected: false,
  isListed: true,
  isTabulated: false
};

function SummaryTable(props: IUtilitySummaryProps) {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    rowData,
    columnDetails,
    fileName: csvFileName,
    costCurrency,
    utilityType,
    isMonthly
  } = props;
  const apiContext = useContext(ApiContext);
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>('accountNumber');

  const mapColumns = (column: IRawTableColumn): ISummaryTableColumn => {
    if (props.utilityType === UtilityType.Electric) {
      return {
        header: column.name,
        unit: column.unit,
        field: column.key,
        //initialWidth: '14rem',
        ...columnsMetaData.find((item) => item.field === column.key)
      };
    } else {
      return {
        header: column.name,
        unit: column.unit,
        field: column.key,
        //initialWidth: '14rem',
        ...columnsMetaDataForOtherUtility.find(
          (item) => item.field === column.key
        )
      };
    }
  };

  const renderTableAction = (celldata: ICellData) => {
    const handleDownload = () => {
      // download pdf link
      const payload = {
        logicalAccountId: celldata.rowData.logicalAccountId,
        accountId: celldata.rowData.accountId
      };
      apiContext &&
        apiContext.handlePDFDownload &&
        apiContext.handlePDFDownload(payload);
      document.getElementById('root')?.click();
    };
    if (
      !celldata.rowData.meterNumber &&
      celldata.rowData.meterNumber !== null &&
      celldata.rowData.isBillUploaded === true
    ) {
      return (
        <Popup
          element={
            <span>
              <Icon root="common" name="cloud-download" size="medium" /> PDF
            </span>
          }
          hoverable={false}
          position="bottom right"
          on="click"
        >
          <VerticalMenu>
            <VerticalMenu.Item disabled={isMonthly} onClick={handleDownload}>
              {t(d.DOWNLOAD_PDF)}
            </VerticalMenu.Item>
          </VerticalMenu>
        </Popup>
      );
    } else {
      return <span>N/A</span>;
    }
  };

  const actionObj = {
    header: 'Action',
    unit: null,
    field: 'action',
    cellDataRenderer: renderTableAction,
    isSelected: false,
    isTabulated: true,
    initialWidth: '6.5rem',
    isListed: false
  };

  const meterObj = {
    header: 'MeterNumber',
    unit: null,
    field: 'meterNumber',
    isSelected: true,
    isTabulated: true,
    initialWidth: '18rem',
    isListed: false,
    width: '15rem'
  };

  const [data, setData] = useState<ISummaryTableDataObject[]>(rowData);
  const [originalFilterData, setOriginalFilterData] = useState<
    ISummaryTableDataObject[]
  >(
    rowData.map((element) => {
      return { ...element, isSelected: true };
    })
  );
  const [columns, setColumns] = useState<ISummaryTableColumn[]>([actionObj]);
  const [fileColumns, setFileColumns] = useState<ISummaryTableColumn[]>([
    meterObj
  ]);
  const [fileData, setFileData] = useState<ISummaryTableDataObject[]>(rowData);

  const filteredData = data.filter(
    (item: ISummaryTableDataObject) => item.isSelected
  );

  const [expandedRows, setexpandedRows] = useState<IAny>([filteredData[0]]);

  const toggleExpand = (
    data: IAny,
    open: boolean,
    expandedRows: IAny[],
    setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
  ) => {
    const expanded = [...expandedRows];
    if (open) {
      let index = expanded.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(data)
      );
      expanded.splice(index, 1);
    } else {
      expanded.push(data);
    }
    setexpandedRows(expanded);
  };

  console.log('props.utilityType', props.utilityType);

  const expanderTemplate = (
    data: ICellData,
    expandedRows: IAny[],
    setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
  ) => {
    const open =
      expandedRows.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(data.rowData)
      ) >= 0;
    return data.rowData.meterDetails && data.rowData.meterDetails.length ? (
      <div
        onClick={() =>
          toggleExpand(data.rowData, open, expandedRows, setexpandedRows)
        }
      >
        <Icon
          root="common"
          size="small"
          name={open ? 'caret-down' : 'caret-right'}
        />
        {'  ' + data.value}
      </div>
    ) : (
      <div>
        <Tooltip
          element={<></>}
          event="hover"
          content={data.value}
          position="right center"
          size="small"
        >
          <img
            className="card-electricity-icon"
            src={iconMapper[props.utilityType ?? '']}
          />
          &nbsp
          {'  ' + data.value}
        </Tooltip>
      </div>
    );
  };

  const renderAccountNumber = (cellData: ICellData) => {
    return expanderTemplate(cellData, expandedRows, setexpandedRows);
  };

  const getMeterName = (cellData: ICellData) => {
    return (
      <div className="MeterName-Utility-Breakdown">
        <Icon
          style={{ visibility: 'hidden' }}
          root="common"
          size="small"
          name={'caret-right'}
        />
        <img
          className="card-electricity-icon"
          src={iconMapper[props.utilityType ?? '']}
        />
        <div className="MeterName">{cellData.value}</div>
      </div>
    );
  };
  const renderMeterNumber = (cellData: ICellData) => {
    return (
      // <div className="MeterName-Utility-Breakdown">
      <>
        <Tooltip
          element={getMeterName(cellData)}
          event="hover"
          content={cellData.value}
          position="right center"
          size="small"
        />
      </>
    );
  };

  const fixedColumns: ISummaryTableColumn[] = [
    {
      header: t(d.ACCOUNT_NUMBER),
      unit: null,
      field: 'accountNumber',
      isSelected: true,
      isTabulated: true,
      initialWidth: '18rem',
      isListed: false,
      width: '15rem'
      // cellDataRenderer: renderAccountNumber
    },
    {
      header: t(d.BILLING_CYCLE),
      unit: null,
      field: 'billingCycle',
      isSelected: true,
      isTabulated: true,
      initialWidth: '14rem',
      isListed: false,
      cellDataRenderer: renderBillingCycle
    },
    {
      header: t(d.PERIOD),
      unit: null,
      field: 'period',
      initialWidth: '8rem',
      isSelected: true,
      isTabulated: true,
      isListed: false
    }
  ];

  useEffect(() => {
    setData(
      rowData.map((element: any) => {
        element.dynamicColumns &&
          element.dynamicColumns.length &&
          element.dynamicColumns.forEach((col: any) => {
            element[col.name] = col.value;
          });
        return {
          ...element,
          isSelected: true
        };
      })
    );
  }, [rowData]);

  useEffect(() => {
    let arr: ISummaryTableDataObject[] = [];
    rowData.map((row) => {
      row.meterDetails &&
        row.meterDetails.length === 1 &&
        row.meterDetails.forEach((m) => {
          arr.push({ ...row, meterNumber: m.meterNumber, isSelected: true });
        });
      let obj = row;
      row.meterDetails.length > 1 && arr.push({ ...row, isSelected: true });
      row.meterDetails.length === 0 && arr.push({ ...row, isSelected: true });
      row.meterDetails &&
        row.meterDetails.length > 1 &&
        row.meterDetails.forEach((meter: IAny) => {
          let newObj;
          if (props.utilityType === UtilityType.Electric) {
            newObj = {
              accountNumber: row.accountNumber,
              meterNumber: meter.meterNumber,
              period: meter.period,
              billingdemand: meter.billingdemand,
              consumption: meter.consumption,
              consumptioncost: meter.consumptioncost,
              demandcharges: meter.demandcharges,
              others: meter.others,
              recordeddemand: meter.recordeddemand,
              servicecharges: meter.servicecharges,
              totalcost: meter.totalcost
            };
          } else {
            newObj = {
              accountNumber: row.accountNumber,
              meterNumber: meter.meterNumber,
              period: meter.period,
              billingdemand: meter.billingdemand,
              consumption: meter.consumption,
              consumptioncharge: meter.consumptioncharge,
              demandcharges: meter.demandcharges,
              others: meter.others,
              recordeddemand: meter.recordeddemand,
              servicecharges: meter.servicecharges,
              totalcost: meter.totalcost
            };
          }

          arr.push({ ...obj, ...newObj, isSelected: true });
        });
    });
    return setFileData(arr);
  }, [rowData]);

  useEffect(() => {
    let columnList =
      columnDetails && columnDetails.length
        ? [
            ...fixedColumns,
            ..._.orderBy(columnDetails.map(mapColumns), ['order', ['asc']])
          ]
        : [...fixedColumns];
    const isOtherColumnsExist =
      columnDetails &&
      columnDetails.length &&
      columnDetails.some((column) => OTHER_COLUMNS.includes(column.key));
    columnList = isOtherColumnsExist
      ? columnList.concat(otherColumnObj)
      : columnList;
    columnList = columnList.concat(actionObj);
    setColumns(columnList);
    let metObj = JSON.parse(JSON.stringify(columnList));
    metObj.splice(1, 0, meterObj);
    setFileColumns(metObj);
  }, [columnDetails]);

  const onColumnSelectorApply = (updatedColumns: ISummaryTableColumn[]) => {
    updatedColumns = updatedColumns.map((item: ISummaryTableColumn) => ({
      ...item,
      isTabulated: item.isSelected
    }));
    setColumns([...fixedColumns, ...updatedColumns, actionObj]);
  };

  const onFilterApply = (filteredData: ISummaryTableDataObject[]) => {
    const tempdata = JSON.parse(JSON.stringify(originalFilterData));

    const emp: IAny = [];
    filteredData.forEach((ft) => {
      tempdata.forEach((ele: IAny) => {
        if (ele.accountNumber === ft.accountNumber && ft.isSelected) {
          emp.push(ele);
          ele.isSelected = true;
        } else if (ele.accountNumber === ft.accountNumber && !ft.isSelected) {
          ele.isSelected = false;
        }
      });
    });
    setData(emp);
    setOriginalFilterData(tempdata);
  };

  const onFilterReset = () => {
    setData((current) =>
      current.map((element) => ({ ...element, isSelected: true }))
    );
  };

  const rowExpansionTemplate = (
    data: IAny,
    columnDetails: IDataTableColumn[],
    rowcellDataRendererForChildren: (cellData: ICellData) => JSX.Element
  ) => {
    return (
      <div className="UtilityBill-row-expansion">
        <DataTable data={data.meterDetails} showHeader={false}>
          <DataTable.Column initialWidth="1.5%" field="" header="" />
          {columns
            .filter((column: ISummaryTableColumn) => column.isTabulated)
            .map((column: ISummaryTableColumn) => {
              if (column.field === 'accountNumber') {
                return (
                  <DataTable.Column
                    align={column.unit ? 'right' : 'left'}
                    key={column.field}
                    className={column.className}
                    field={column.field}
                    header={column.header}
                    sortable={column.sortable}
                    renderer={renderMeterNumber}
                    initialWidth={column.initialWidth}
                  />
                );
              }
              return (
                <DataTable.Column
                  align={column.unit ? 'right' : 'left'}
                  key={column.field}
                  className={column.className}
                  field={column.field}
                  header={column?.header}
                  sortable={column.sortable}
                  renderer={column.cellDataRenderer}
                  initialWidth={column.initialWidth}
                />
              );
            })}
        </DataTable>
      </div>
    );
  };

  const costFields = [
    'totalcost',
    'consumptioncost',
    'demandcharges',
    'servicecharges',
    'fixed',
    'taxes',
    'penalties',
    'others',
    'transmission&distribution',
    't&dcost'
  ];

  const addUnits = (filteredData: any) => {
    const modifiedFilteredDate: IAny = [];
    filteredData.forEach((row: IAny) => {
      if (
        row.billingdemand &&
        row.billingdemand.toString().split(' ').length === 1
      ) {
        row.billingdemand =
          row.billingdemand + ' ' + row.scaledBillingDemandUnit;
      }
      if (
        row.consumption &&
        row.consumption.toString().split(' ').length === 1
      ) {
        row.consumption = row.consumption + ' ' + row.scaledConsumptionUnit;
      }
      if (
        row.recordeddemand &&
        row.recordeddemand.toString().split(' ').length === 1
      ) {
        row.recordeddemand =
          row.recordeddemand + ' ' + row.scaledRecordDemandUnit;
      }
      if (row.meterDetails && row.meterDetails.length) {
        row.meterDetails = addUnits(row.meterDetails);
      }

      row.unitsApplied = true;
      modifiedFilteredDate.push(row);
    });
    // filteredData.forEach((row: any) => {
    //   columns.forEach((cl: IAny) => {
    //     if (Object.keys(row).includes(cl.field) && !row.unitsApplied) {
    //       if (cl && cl.unit) {
    //         if (costFields.includes(cl.field)) {
    //           row[cl.field] = cl.unit + ' ' + row[cl.field];
    //         } else {
    //           row[cl.field] = row[cl.field] + ' ' + cl.unit;
    //         }
    //       }
    //     }
    //   });
    //   if (row.meterDetails && row.meterDetails.length) {
    //     row.meterDetails = addUnits(row.meterDetails);
    //   }

    //   row.unitsApplied = true;
    //   modifiedFilteredDate.push(row);
    // });
    return modifiedFilteredDate;
  };

  return (
    <Card className="utility-bill-summary-table">
      {/* <Card.Header className="table-header-div">{t(d.SUMMARY)}</Card.Header> */}
      <div className="Utility-Summary-Subheader">
        {t(descriptors.utilityBreakDown.SUMMARYTABLESUBHEADER)}
      </div>
      <Card.Content>
        <DataTable
          className="summary-table"
          data={addUnits(filteredData)}
          rowExpansionTemplate={rowExpansionTemplate as any}
          expandedRows={expandedRows}
          // reorderableColumns={true}
          resizableColumns={true}
          scrollable={true}
          scrollWidth="93vw"
          onSort={({ sortField, sortOrder }) => {
            setFieldSortOrder(sortOrder as number);
            setSortFieldName(sortField);
          }}
          sortOrder={fieldSortOrder}
          sortField={sortFieldName}
        >
          <DataTable.HeaderBar>
            <DataTable.HeaderBar.Item
              content={t(d.SUMMARY)}
              className="table-title"
            />
            <div className="table-menu">
              <CSVDownloadComponent
                csvFileName={csvFileName}
                costCurrency={costCurrency}
                data={fileData.filter(
                  (row: ISummaryTableDataObject) => row.isSelected
                )}
                columns={fileColumns.filter(
                  (column: ISummaryTableColumn) => column.isTabulated
                )}
              />
              <FilterComponent
                data={originalFilterData.filter(
                  (dt, ind) =>
                    originalFilterData.findIndex(
                      (d) => d.accountNumber === dt.accountNumber
                    ) === ind
                )}
                onApply={onFilterApply}
                onReset={onFilterReset}
              />
              {props.utilityType === UtilityType.Electric && (
                <ColumnSelectorComponent
                  data={columns.filter(
                    (column: ISummaryTableColumn) => column.isListed
                  )}
                  onApply={onColumnSelectorApply}
                />
              )}
            </div>
          </DataTable.HeaderBar>
          {columns
            .filter((column: ISummaryTableColumn) => column.isTabulated)
            .map((column: ISummaryTableColumn) => {
              if (column.field === 'accountNumber') {
                return (
                  <DataTable.Column
                    align={column.unit ? 'right' : 'left'}
                    key={column.field}
                    className={column.className}
                    field={column.field}
                    header={column.header}
                    sortable={column.sortable}
                    renderer={renderAccountNumber}
                    initialWidth={column.initialWidth}
                  />
                );
              }
              return (
                <DataTable.Column
                  align={column.unit ? 'right' : 'left'}
                  key={column.field}
                  className={column.className}
                  field={column.field}
                  header={column.header}
                  sortable={column.sortable}
                  renderer={column.cellDataRenderer}
                  initialWidth={column.initialWidth}
                />
              );
            })}
          {/* <DataTable.Pagination
            totalItems={filteredData?.length}
            itemsPerPage={10}
            className="summary-table-pagination"
          /> */}
        </DataTable>
      </Card.Content>
    </Card>
  );
}

export default SummaryTable;
